"use client";

import { useState, useEffect } from "react";
import {
  buildWeeklySummary,
  buildCategorySummary,
  WeeklySummary,
  WeeklyCategoryGroup,
} from "@/lib/summary";

interface WeeklySummaryPanelProps {
  onClose: () => void;
  refreshKey?: number;
}

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onRangeChange: (start: Date, end: Date) => void;
}

function DateRangePicker({
  startDate,
  endDate,
  onRangeChange,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState(
    startDate.toISOString().split("T")[0],
  );
  const [tempEnd, setTempEnd] = useState(endDate.toISOString().split("T")[0]);

  const handleApply = () => {
    onRangeChange(new Date(tempStart), new Date(tempEnd));
    setIsOpen(false);
  };

  const formatRange = () => {
    const start = startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${start} - ${end}`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-xs bg-white/80 hover:bg-white px-2 py-1 rounded border border-gray-200 flex items-center gap-1 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-gray-600">{formatRange()}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50 min-w-[200px]">
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={tempStart}
                onChange={(e) => setTempStart(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-blue"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={tempEnd}
                onChange={(e) => setTempEnd(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-blue"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 text-xs py-1 px-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="flex-1 text-xs py-1 px-2 bg-brand-blue text-white rounded hover:bg-brand-blue/90 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WeeklySummaryPanel({
  onClose,
  refreshKey = 0,
}: WeeklySummaryPanelProps) {
  const [summary, setSummary] = useState<WeeklySummary | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [expandedOccasions, setExpandedOccasions] = useState<Set<string>>(
    new Set(),
  );
  const [categoryDateRanges, setCategoryDateRanges] = useState<
    Map<string, { start: Date; end: Date }>
  >(new Map());
  const [customCategorySummaries, setCustomCategorySummaries] = useState<
    Map<string, WeeklyCategoryGroup>
  >(new Map());

  useEffect(() => {
    const weeklySummary = buildWeeklySummary();
    setSummary(weeklySummary);

    // Auto-expand all categories
    const allCategories = new Set(
      weeklySummary.categories.map((c) => c.category),
    );
    setExpandedCategories(allCategories);

    // Auto-expand all occasions
    const allOccasions = new Set<string>();
    weeklySummary.categories.forEach((cat) => {
      cat.occasions.forEach((occ) => {
        allOccasions.add(`${cat.category}-${occ.occasion}`);
      });
    });
    setExpandedOccasions(allOccasions);
  }, [refreshKey]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleOccasion = (key: string) => {
    setExpandedOccasions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCategoryDateRangeChange = (
    category: string,
    start: Date,
    end: Date,
  ) => {
    setCategoryDateRanges((prev) => {
      const next = new Map(prev);
      next.set(category, { start, end });
      return next;
    });

    // Rebuild summary for this category with new date range
    const catSummary = buildCategorySummary(category, start, end);
    if (catSummary) {
      setCustomCategorySummaries((prev) => {
        const next = new Map(prev);
        next.set(category, catSummary);
        return next;
      });
    }
  };

  const getCategoryData = (
    category: string,
  ): WeeklyCategoryGroup | undefined => {
    // Use custom summary if available, otherwise use default
    return (
      customCategorySummaries.get(category) ||
      summary?.categories.find((c) => c.category === category)
    );
  };

  const getCategoryDateRange = (category: string) => {
    return (
      categoryDateRanges.get(category) ||
      summary?.dateRange || { start: new Date(), end: new Date() }
    );
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-cyan to-brand-blue p-4 shadow-md">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-white font-semibold text-lg">
            📈 Weekly Overview
          </h2>
          <div className="w-10" />
        </div>

        {summary && (
          <div className="text-white/80 text-sm text-center">
            {summary.totalNotes} notes from{" "}
            {summary.dateRange.start.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}{" "}
            to{" "}
            {summary.dateRange.end.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </div>
        )}
      </div>

      {/* Summary content */}
      <div className="flex-1 overflow-y-auto p-4">
        {!summary || summary.totalNotes === 0 ? (
          <div className="text-center text-gray-500 mt-12">
            <div className="text-5xl mb-3">📭</div>
            <p className="font-medium">No notes in the past 7 days</p>
            <p className="text-sm mt-1">
              Create some notes to see your weekly summary
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {summary.categories.map((category) => {
              const catData = getCategoryData(category.category);
              if (!catData) return null;
              const isExpanded = expandedCategories.has(category.category);
              const dateRange = getCategoryDateRange(category.category);

              return (
                <div
                  key={category.category}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-gradient-to-r from-brand-cyan/5 to-brand-blue/5">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleCategory(category.category)}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                      >
                        <span className="text-lg">📁</span>
                        <span className="font-semibold text-gray-800">
                          {category.category}
                        </span>
                        <span className="text-xs bg-brand-blue/10 text-brand-blue px-2 py-0.5 rounded-full">
                          {catData.occasions.reduce(
                            (acc, o) =>
                              acc +
                              o.topics.reduce((a, t) => a + t.notes.length, 0),
                            0,
                          )}{" "}
                          notes
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <DateRangePicker
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                        onRangeChange={(start, end) =>
                          handleCategoryDateRangeChange(
                            category.category,
                            start,
                            end,
                          )
                        }
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-3 space-y-3">
                      {catData.occasions.map((occasion) => {
                        const occasionKey = `${category.category}-${occasion.occasion}`;
                        const isOccasionExpanded =
                          expandedOccasions.has(occasionKey);

                        return (
                          <div
                            key={occasion.occasion}
                            className="border-l-2 border-brand-cyan/30 pl-3"
                          >
                            <button
                              onClick={() => toggleOccasion(occasionKey)}
                              className="w-full flex items-center justify-between py-1 hover:bg-gray-50 rounded transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm">📌</span>
                                <span className="font-medium text-gray-700 text-sm">
                                  {occasion.occasion}
                                </span>
                              </div>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 text-gray-400 transition-transform ${isOccasionExpanded ? "rotate-180" : ""}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>

                            {isOccasionExpanded && (
                              <div className="mt-2 space-y-2 ml-2">
                                {occasion.topics.map((topic) => (
                                  <div
                                    key={topic.topic}
                                    className="bg-notepad-yellow/50 rounded-lg p-3 border border-notepad-line/30"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs">📝</span>
                                        <span className="font-medium text-gray-800 text-sm">
                                          {topic.topic}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          ({topic.notes.length} note
                                          {topic.notes.length !== 1 ? "s" : ""})
                                        </span>
                                      </div>
                                    </div>

                                    {topic.summary && (
                                      <div className="text-xs text-gray-600 bg-white/50 rounded p-2 mb-2 italic whitespace-pre-wrap">
                                        💡 {topic.summary}
                                      </div>
                                    )}

                                    <div className="space-y-1">
                                      {topic.notes.map((note) => (
                                        <div
                                          key={note.id}
                                          className="text-xs text-gray-500 flex items-center gap-2"
                                        >
                                          <span className="text-gray-400 whitespace-nowrap">
                                            {formatTime(note.createdAt)}
                                          </span>
                                          <span className="text-gray-600 truncate">
                                            {note.content.substring(0, 60)}
                                            {note.content.length > 60
                                              ? "..."
                                              : ""}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
